import type { EditableSession } from '~/pages/backoffice/admin/sessions/components/SessionModal'
import { useMutation, useQuery } from '@apollo/client/react'
import { Loader, MoreHorizontal, PlayCircle, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_GROUPS } from '~/gql/groups/groups'
import { GQL_SCORM_MODULES } from '~/gql/scorm-modules/scorm-modules'
import { GQL_DELETE_SESSION, GQL_SESSIONS } from '~/gql/sessions/sessions'
import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import SessionModal from '~/pages/backoffice/admin/sessions/components/SessionModal'

function SessionsTable() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, loading, refetch } = useQuery(GQL_SESSIONS, { fetchPolicy: 'cache-and-network' })
  const { data: modulesData } = useQuery(GQL_SCORM_MODULES)
  const { data: usersData } = useQuery(GQL_USERS_WITH_FILTERS, {
    variables: { input: { skip: 0, take: 1000, searchInput: '' } },
  })
  const { data: groupsData } = useQuery(GQL_GROUPS)
  const [deleteSession] = useMutation(GQL_DELETE_SESSION)

  const [openModal, setOpenModal] = useState(false)
  const [editing, setEditing] = useState<EditableSession | undefined>()

  const sessions = data?.sessions ?? []
  const readyModules = (modulesData?.scormModules ?? []).filter(m => m.status === 'READY')
  const users = usersData?.getFilteredUsers?.users ?? []
  const groups = groupsData?.groups ?? []

  const openCreate = () => {
    setEditing(undefined)
    setOpenModal(true)
  }

  const openEdit = (session: EditableSession) => {
    setEditing(session)
    setOpenModal(true)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteSession({ variables: { id } })
      toast.success(t('sessions.deleted'))
      await refetch()
    } catch {
      toast.error(t('sessions.deleteFailed'))
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>{t('sessions.list.title')}</CardTitle>
          <Button onClick={openCreate}>
            <Plus className="mr-2 size-4" />
            {t('sessions.new')}
          </Button>
        </CardHeader>
        <CardContent>
          {loading && sessions.length === 0
            ? <div className="flex justify-center p-6"><Loader className="size-6 animate-spin" /></div>
            : sessions.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('sessions.empty')}</p>
              : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('common.module')}</TableHead>
                        <TableHead>{t('common.participants')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                        <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">{s.name}</TableCell>
                          <TableCell>
                            {/* Le titre du premier module suffit à identifier le
                                parcours ; le compte dit le reste sans allonger
                                la colonne. Le détail est dans le modal. */}
                            <span className="block max-w-[18rem] truncate">
                              {s.modules?.[0]?.module?.title ?? '—'}
                            </span>
                            {(s.modules?.length ?? 0) > 1 && (
                              <span className="text-xs text-muted-foreground">
                                {t('sessions.path.count', { count: s.modules!.length })}
                                {s.progressionMode === 'SEQUENTIAL' && ` · ${t('sessions.progression.sequential')}`}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{s.enrolledUsers?.length ?? 0}</TableCell>
                          <TableCell>
                            <Badge variant={s.status === 'ACTIVE' ? 'default' : 'secondary'}>
                              {s.status === 'ACTIVE' ? t('status.active') : t('status.archived')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="size-8 p-0">
                                  <span className="sr-only">{t('common.action')}</span>
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEdit(s)}>
                                  {t('common.update')}
                                </DropdownMenuItem>
                                {s.modules?.[0]?.module?.id && (
                                  <DropdownMenuItem onClick={() => navigate(`/scorm/preview/${s.modules![0]!.module!.id}`)}>
                                    <PlayCircle className="mr-2 size-4" />
                                    {t('sessions.openModule')}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => handleDelete(s.id)}
                                >
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
        </CardContent>
      </Card>

      {openModal && (
        <SessionModal
          open={openModal}
          onCancel={() => { setOpenModal(false); setEditing(undefined) }}
          onSuccess={refetch}
          modules={readyModules}
          users={users}
          groups={groups}
          session={editing}
        />
      )}
    </div>
  )
}

export default SessionsTable
